import { routes } from '@/shared/routes'
import type { SearchIntent } from '../models/search-intent.model'
import type { SearchTab } from '../types/search.types'

/**
 * The public results page's filter chips. `shop` and `profinder` are this
 * page's own: the Shop comes from the standalone Shop's public products API
 * (ADR-0132) and ProFinder shows the service rows that lead there.
 */
export type PublicSearchFilter =
  | 'all'
  | 'hives'
  | 'shop'
  | 'profinder'
  | 'businesses'
  | 'events'
  | 'people'
  | 'posts'

export const PUBLIC_SEARCH_FILTERS: readonly PublicSearchFilter[] = [
  'all',
  'hives',
  'shop',
  'profinder',
  'businesses',
  'events',
]
export const PRIVATE_SEARCH_FILTERS: readonly PublicSearchFilter[] = [
  ...PUBLIC_SEARCH_FILTERS,
  'people',
  'posts',
]

/** Coerce a `?tab=` value to a filter the viewer may use; unknown → `all`. */
export function parsePublicSearchFilter(value: string | undefined, isPublic: boolean): PublicSearchFilter {
  const allowed = isPublic ? PUBLIC_SEARCH_FILTERS : PRIVATE_SEARCH_FILTERS
  return allowed.includes(value as PublicSearchFilter) ? (value as PublicSearchFilter) : 'all'
}

/** Which Go-backed collection a filter reads; `null` when it reads only the Shop. */
export function hubTabForFilter(filter: PublicSearchFilter): SearchTab | null {
  switch (filter) {
    case 'shop':
      return null
    case 'profinder':
      return 'services'
    default:
      return filter
  }
}

export function searchHref(query: string, tab: PublicSearchFilter = 'all'): string {
  const params = new URLSearchParams({ q: query.trim() })
  if (tab !== 'all') params.set('tab', tab)
  return `${routes.search}?${params}`
}

/**
 * The page a submitted query opens for each intent, with the query already in
 * that app's search box: the Shop's search, ProFinder (with the session zip),
 * the business directory, the hives list, or this site's all-results page.
 */
export function searchIntentHref(intent: SearchIntent, query: string, zip?: string): string {
  const q = query.trim()
  switch (intent) {
    case 'shop':
      return routes.shopSite.search(q)
    case 'profinder':
      return routes.profinder.index({ q, zip, autoSearch: true })
    case 'business':
      return routes.businesses({ q })
    case 'hive':
      return routes.hivesSearch(q)
    default:
      return searchHref(q)
  }
}

export const isExternalHref = (href: string) => /^https?:\/\//i.test(href)
